import { Accordion, Group, Stack, Text } from '@mantine/core'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { DeploymentStatusIndicator } from '@/components/deployments/deployment-list/DeploymentList/DeploymentStatusIndicator/DeploymentStatusIndicator'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { DeploymentStatus } from '@/types/deployment'
import { DeploymentGroup } from '@/types/deploymentGroup'
import { Device } from '@/types/device'
import { getDevicesCameras } from '@/utils/device/getDeviceAttr'
import { DeploymentCameraImage } from './DeploymentCameraImage'
import { DeploymentCameraInfo } from './DeploymentCameraInfo'
import { DeploymentRecentEventsTable } from './DeploymentRecentEventsTable'
import { formatRate, getMemberRate } from './memberRate'

type DeploymentGroupCamerasTabProps = {
  group: DeploymentGroup
  device: Device
  appId: string
  initialDeploymentId?: string
  onRefresh: () => void
}

export const DeploymentGroupCamerasTab = ({
  group,
  device,
  appId,
  initialDeploymentId,
  onRefresh
}: DeploymentGroupCamerasTabProps) => {
  const [openedId, setOpenedId] = useState<string | null>(
    initialDeploymentId ?? null
  )
  const cameras = getDevicesCameras(device)
  const members = [...group.members].sort(
    (a, b) => a.stream_index - b.stream_index
  )

  return (
    <Accordion variant="separated" value={openedId} onChange={setOpenedId}>
      {members.map((member) => {
        const camera = cameras.find((c) => c.camera_id === member.stream_id)
        const rate = getMemberRate(group, member)

        return (
          <Accordion.Item
            key={member.deployment_id}
            value={member.deployment_id}
          >
            <Accordion.Control>
              <Group justify="space-between" wrap="nowrap" pr="sm">
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm" fw={600}>
                    {camera?.camera_name ?? member.stream_id}
                  </Text>
                  {rate.fps !== null && (
                    <Text size="xs" c="dimmed">
                      {rate.source_fps !== null ? (
                        <FormattedMessage
                          id="deployments.groups.details.cameras.fps"
                          values={{
                            fps: formatRate(rate.fps),
                            source: formatRate(rate.source_fps)
                          }}
                        />
                      ) : (
                        <FormattedMessage
                          id="deployments.groups.details.cameras.fpsOnly"
                          values={{ fps: formatRate(rate.fps) }}
                        />
                      )}
                    </Text>
                  )}
                </Group>
                <DeploymentStatusIndicator
                  status={member.status}
                  statusUpdatedAt={member.status_updated_at}
                  deviceActive={device.active}
                />
              </Group>
            </Accordion.Control>

            {/* Mantine 7 Accordion has no keepMounted; render only the opened camera so closed ones never query. */}
            <Accordion.Panel>
              {openedId === member.deployment_id && (
                <Stack gap="xs">
                  {member.error_body && (
                    <PlatformAlert
                      variant={
                        member.status === DeploymentStatus.Failed
                          ? 'error'
                          : 'warning'
                      }
                    >
                      {member.error_body}
                    </PlatformAlert>
                  )}

                  {camera ? (
                    <>
                      <DeploymentCameraInfo
                        camera={camera}
                        device={device}
                        onRefresh={onRefresh}
                      />
                      <DeploymentCameraImage
                        src={camera.image}
                        cameraName={camera.camera_name}
                      />
                      <DeploymentRecentEventsTable
                        appId={appId}
                        camera={camera}
                      />
                    </>
                  ) : (
                    <Text size="sm" c="dimmed">
                      <FormattedMessage id="deployments.groups.details.cameras.notOnDevice" />
                    </Text>
                  )}
                </Stack>
              )}
            </Accordion.Panel>
          </Accordion.Item>
        )
      })}
    </Accordion>
  )
}
